/**
 * Байты картинки: магические байты, размеры из заголовка, лимиты. §Ключ 3.4 (шаг 19),
 * §Ключ 5.1 шаг 20, docs/phase-2/08-visuals.md §7.
 *
 * ПОЧЕМУ ЭТО ЖИВЁТ В КОНТРАКТАХ, А НЕ В ПУБЛИКАТОРЕ. Разбор писался для шага проверки
 * конверта перед отправкой, и там он остаётся нужен. Но с §7 те же три лимита обязан
 * применять БЭКЕНД — на приёме байтов, а не публикатор после одобрения: картинку 20 000 px
 * шириной, отбитую публикатором, человек уже утвердил, и отказ стоит дороже всего именно
 * там. Импортировать соседний сервис бэкенду нельзя (правило R5), поэтому у знания о байтах
 * ровно один дом, и он здесь.
 *
 * Файл достижим из входа `.`, поэтому в нём нет ни zod, ни `node:*` — только DataView и
 * Uint8Array (правила R8/R1). Buffer — это Uint8Array, поэтому вызовы из бэкенда и
 * публикатора компилируются без приведения.
 *
 * Разбор намеренно минимальный: PNG — IHDR по фиксированному смещению, JPEG — проход по
 * маркерам до первого SOFn. Полноценный декодер здесь не нужен и был бы новой зависимостью.
 */

/**
 * ПОТОЛОК TELEGRAM на фотографию, 10 МиБ. Имя названо стороной, которая его ставит, и это
 * не косметика: `@smm/contracts` уже экспортирует `MEDIA_MAX_BYTES = 8 388 608` из
 * `api/intake.ts` — потолок ТЕЛА нашего маршрута. Два разных числа под одним именем в одном
 * пакете — это ошибка, которую поймает не компилятор, а канал.
 */
export const TELEGRAM_PHOTO_MAX_BYTES = 10_485_760;
export const TELEGRAM_PHOTO_MAX_DIMENSION_SUM = 10_000;
export const TELEGRAM_PHOTO_MAX_ASPECT_RATIO = 20;

/**
 * Потолок ЧЕЛОВЕЧЕСКОЙ загрузки (`POST /api/v1/media`), 5 МиБ — §7. Он ниже и телеграмного,
 * и потолка тела intake, и это осознанно: человек кладёт файл из браузера, и отказ обязан
 * прийти словами с числом, а не обрывом соединения на восьмом мегабайте.
 */
export const HUMAN_UPLOAD_MAX_BYTES = 5_242_880;

/**
 * Потолок документа (PDF внизу поста, docs/release-2026-09-19.md §9.2), 20 МиБ. Потолок Bot API
 * для файла, который бот загружает сам, — 50 МБ; ниже он взят ради диска дроплета и времени
 * одной попытки публикации (`SLOT_DEADLINE_S` = 300: файл уходит в той же попытке, что и
 * текст). Число повторено CHECK'ом `media_asset` в миграции 0020 и потолком чтения
 * `GET /dispatch/v1/media/:id` у публикатора.
 */
export const HUMAN_DOCUMENT_MAX_BYTES = 20_971_520;

export const PNG_MAGIC = Uint8Array.from([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
export const JPEG_MAGIC = Uint8Array.from([0xff, 0xd8, 0xff]);
/** `%PDF-` — первые пять байт любого PDF. Проверяется на приёме вместо доверия заявленному типу. */
export const PDF_MAGIC = Uint8Array.from([0x25, 0x50, 0x44, 0x46, 0x2d]);

export type DocumentMime = 'application/pdf';

export function isPdfBytes(bytes: Uint8Array): boolean {
  return startsWith(bytes, PDF_MAGIC);
}

export type MediaMime = 'image/png' | 'image/jpeg';

export function isMediaMime(v: string): v is MediaMime {
  return v === 'image/png' || v === 'image/jpeg';
}

function startsWith(bytes: Uint8Array, magic: Uint8Array): boolean {
  if (bytes.length < magic.length) return false;
  for (let i = 0; i < magic.length; i++) {
    if (bytes[i] !== magic[i]) return false;
  }
  return true;
}

/** Заявленный mime против содержимого. Расхождение → MEDIA_MIME_MISMATCH. */
export function magicMatches(bytes: Uint8Array, mime: MediaMime): boolean {
  return mime === 'image/png' ? startsWith(bytes, PNG_MAGIC) : startsWith(bytes, JPEG_MAGIC);
}

export interface Dimensions {
  readonly width: number;
  readonly height: number;
}

function pngDimensions(bytes: Uint8Array): Dimensions | undefined {
  // 8 байт сигнатуры + 4 длины + 4 типа ('IHDR') + width(4) + height(4) = минимум 24.
  if (bytes.length < 24) return undefined;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(12, false) !== 0x49484452) return undefined; // 'IHDR'
  return { width: view.getUint32(16, false), height: view.getUint32(20, false) };
}

/** Маркеры SOF, у которых в теле лежат размеры. DHT/DAC/RSTn/SOS размеров не несут. */
const JPEG_SOF_MARKERS: ReadonlySet<number> = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
]);

function jpegDimensions(bytes: Uint8Array): Dimensions | undefined {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let i = 2; // пропускаем SOI (ff d8)
  while (i + 3 < bytes.length) {
    if (bytes[i] !== 0xff) {
      i += 1;
      continue;
    }
    const marker = bytes[i + 1] as number;
    // Заполнитель 0xff и «standalone»-маркеры без длины пропускаем по одному байту.
    if (marker === 0xff) {
      i += 1;
      continue;
    }
    if (marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd9) || marker === 0x01) {
      i += 2;
      continue;
    }
    if (i + 3 >= bytes.length) return undefined;
    const segLen = view.getUint16(i + 2, false);
    if (JPEG_SOF_MARKERS.has(marker)) {
      // тело SOF: precision(1) height(2) width(2)
      if (i + 9 > bytes.length) return undefined;
      return { height: view.getUint16(i + 5, false), width: view.getUint16(i + 7, false) };
    }
    if (segLen < 2) return undefined;
    i += 2 + segLen;
  }
  return undefined;
}

export function imageDimensions(bytes: Uint8Array, mime: MediaMime): Dimensions | undefined {
  return mime === 'image/png' ? pngDimensions(bytes) : jpegDimensions(bytes);
}

export type GeometryVerdict = { ok: true; dims: Dimensions } | { ok: false; detail: string };

/**
 * Шаг 20. Лимиты — из `07-publisher.md` §5 п. 8: это ограничения самого Telegram, и пост,
 * их нарушающий, получит 400 ПОСЛЕ одобрения — то есть отказ до отправки здесь дешевле.
 */
export function checkGeometry(bytes: Uint8Array, mime: MediaMime): GeometryVerdict {
  if (bytes.length > TELEGRAM_PHOTO_MAX_BYTES) {
    return {
      ok: false,
      detail: `${String(bytes.length)} байт > ${String(TELEGRAM_PHOTO_MAX_BYTES)}`,
    };
  }
  const dims = imageDimensions(bytes, mime);
  if (dims === undefined) return { ok: false, detail: `размеры ${mime} не читаются` };
  if (dims.width <= 0 || dims.height <= 0) {
    return { ok: false, detail: `нулевая сторона: ${String(dims.width)}x${String(dims.height)}` };
  }
  if (dims.width + dims.height > TELEGRAM_PHOTO_MAX_DIMENSION_SUM) {
    return {
      ok: false,
      detail: `width+height = ${String(dims.width + dims.height)} > ${String(TELEGRAM_PHOTO_MAX_DIMENSION_SUM)}`,
    };
  }
  const ratio = Math.max(dims.width, dims.height) / Math.min(dims.width, dims.height);
  if (ratio > TELEGRAM_PHOTO_MAX_ASPECT_RATIO) {
    return {
      ok: false,
      detail: `соотношение сторон ${ratio.toFixed(2)} > ${String(TELEGRAM_PHOTO_MAX_ASPECT_RATIO)}`,
    };
  }
  return { ok: true, dims };
}
